import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Combobox,
  ETypeOfNotification,
  GetItemsProps,
  InputSwitch,
  InputTable,
  InputTextCustom,
  Modal,
  setNotification,
  InputColumnTableProps,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import { defaultComboDataDTO } from '../../dto/ComboDataDTO'
import useResizeListener from '../../hooks/useResizeListener'
import {
  defaultKitDetailDTO,
  KitDetailDTO,
  KitItemDetailDTO,
  KitTypeOptionDTO,
  StockItemPresentationOptionDTO,
} from '../../dto/kits/KitsDTO'
import {
  buildKitSavePayload,
  getKitById,
  getKitStockItemPresentations,
  getKitTypes,
  saveKit,
} from '../../services/KitsController'
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions'
import LocationMobileActionMenu, { LocationMobileActionItem } from '../Location/tabs/components/LocationMobileActionMenu'
import './Kits.scss'

interface KitsFormViewProps {
  kitId: number
  setKitId: (kitId: number | null) => void
  onSaved?: () => void
}

interface KitsFormErrors {
  description?: string
  stockItemsKitType?: string
  detailItem?: string
  detailQuantity?: string
  detailDraft?: string
}

type DetailDraftKey = string | null
const EMPTY_ITEM_FILTER_BATCH_SIZE = 250

function createEmptyDetail(localKey: string): KitItemDetailDTO {
  return {
    localKey,
    stockItemsKitDetailId: null,
    stockItemPresentationId: null,
    itemLabel: '',
    quantity: '',
    mainItem: false,
    stockItemPresentation: null,
  }
}

function normalizeQuantityInput(value: string) {
  return value.replace(',', '.').replace(/[^0-9.]/g, '')
}

function formatQuantityValue(value: string | number | null | undefined) {
  const normalized = String(value ?? '').trim().replace(',', '.')
  if (normalized === '') return ''
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed)) return String(value ?? '').trim()
  return parsed.toString().replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')
}

function formatQuantityForDisplay(value: string | number | null | undefined) {
  return formatQuantityValue(value).replace('.', ',')
}

function parseQuantity(value: string | number | null | undefined) {
  const normalized = String(value ?? '').trim().replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function isIntegerQuantity(value: string | number | null | undefined) {
  const normalized = String(value ?? '').trim().replace(',', '.')
  if (normalized === '') return false
  return /^\d+$/.test(normalized)
}

function formatCardValue(value: string | null | undefined, emptyText = 'Sin datos') {
  const normalized = String(value ?? '').trim()
  return normalized !== '' ? normalized : emptyText
}

function normalizeDecimalInput(value: string): string {
  return value.replace(/[^0-9.,]/g, '');
}

export default function KitsFormView({
  kitId,
  setKitId,
  onSaved,
}: Readonly<KitsFormViewProps>) {
  const dispatch = useDispatch()
  const isMobile = useResizeListener(768)
  const [form, setForm] = useState<KitDetailDTO>(defaultKitDetailDTO)
  const [loading, setLoading] = useState(kitId > 0)
  const [saving, setSaving] = useState(false)
  const [kitTypeOptions, setKitTypeOptions] = useState<KitTypeOptionDTO[]>([])
  const [detailDraft, setDetailDraft] = useState<KitItemDetailDTO>(createEmptyDetail('new'))
  const [detailEditKey, setDetailEditKey] = useState<DetailDraftKey>(null)
  const [errors, setErrors] = useState<KitsFormErrors>({})
  const [detailMobileModalVisible, setDetailMobileModalVisible] = useState(false)
  const detailSectionRef = useRef<HTMLDivElement | null>(null)
  const localKeySequenceRef = useRef(0)

  const title = kitId > 0 ? 'Editar kit' : 'Nuevo kit'
  const isEditingDetail = detailEditKey !== null

  function nextLocalKey(prefix: string) {
    localKeySequenceRef.current += 1
    return `${prefix}-${localKeySequenceRef.current}`
  }

  useEffect(() => {
    let cancelled = false

    async function loadData() {
      setLoading(kitId > 0)
      try {
        const [types, detail] = await Promise.all([
          getKitTypes(),
          kitId > 0 ? getKitById(kitId) : Promise.resolve(defaultKitDetailDTO),
        ])

        if (cancelled) return

        const normalizedDetail = kitId > 0
          ? {
            ...detail,
            stockItemsKitType: detail.stockItemsKitType == null
              ? null
              : types.find((type) => type.id === detail.stockItemsKitType?.id) ?? detail.stockItemsKitType,
          }
          : defaultKitDetailDTO

        setKitTypeOptions(types)
        setForm(normalizedDetail)
        setDetailDraft(createEmptyDetail(nextLocalKey('draft')))
        setDetailEditKey(null)
        setErrors({})
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          dispatch(setNotification({
            title: 'Error',
            desc: 'No se pudieron cargar los datos del kit.',
            typeOfNotification: ETypeOfNotification.ERROR,
            icon: 'pi pi-times',
          }))
          setKitId(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadData()
    return () => {
      cancelled = true
    }
  }, [dispatch, kitId, setKitId])

  function updateField<K extends keyof KitDetailDTO>(field: K, value: KitDetailDTO[K]) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function clearDetailErrors() {
    setErrors((current) => ({
      ...current,
      detailItem: undefined,
      detailQuantity: undefined,
      detailDraft: undefined,
    }))
  }

  function resetDetailDraft() {
    setDetailDraft(createEmptyDetail(nextLocalKey('draft')))
    setDetailEditKey(null)
    clearDetailErrors()
  }

  function getDetailStockItemPresentations(props: GetItemsProps) {
    const hasFilter = String(props?.filterValue ?? '').trim() !== ''
    return getKitStockItemPresentations({
      ...props,
      pageSize: hasFilter ? props.pageSize : Math.max(props.pageSize, EMPTY_ITEM_FILTER_BATCH_SIZE),
    })
  }

  function validateDetailDraft() {
    const nextErrors: KitsFormErrors = {}
    const selectedItemId = detailDraft.stockItemPresentation?.id ?? detailDraft.stockItemPresentationId
    const parsedQuantity = parseQuantity(detailDraft.quantity)

    if (selectedItemId == null || selectedItemId <= 0) {
      nextErrors.detailItem = 'El item es obligatorio.'
    }

    if (detailDraft.quantity.trim() === '') {
      nextErrors.detailQuantity = 'La cantidad es obligatoria.'
    } else if (!isIntegerQuantity(detailDraft.quantity)) {
      nextErrors.detailQuantity = 'La cantidad debe ser un número entero.'
    } else if (parsedQuantity == null || parsedQuantity <= 0 || parsedQuantity > 999999999) {
      nextErrors.detailQuantity = 'La cantidad debe ser un entero mayor a 0 y menor o igual a 999999999.'
    }

    const duplicated = form.details.some((item) => {
      if (detailEditKey != null && item.localKey === detailEditKey) {
        return false
      }
      return item.stockItemPresentationId === selectedItemId
    })

    if (duplicated) {
      nextErrors.detailItem = 'El item ya existe en el detalle del kit.'
    }

    return nextErrors
  }

  function upsertDetailDraft() {
    const validationErrors = validateDetailDraft()
    if (Object.keys(validationErrors).length > 0) {
      setErrors((current) => ({ ...current, ...validationErrors }))
      return false
    }

    const selectedPresentation = detailDraft.stockItemPresentation as StockItemPresentationOptionDTO
    const nextDetail: KitItemDetailDTO = {
      ...detailDraft,
      stockItemPresentationId: selectedPresentation.id,
      itemLabel: selectedPresentation.fullDescription ?? selectedPresentation.name ?? '',
      quantity: formatQuantityValue(detailDraft.quantity),
      stockItemPresentation: selectedPresentation,
    }

    setForm((current) => ({
      ...current,
      details: detailEditKey == null
        ? [...current.details, nextDetail]
        : current.details.map((item) => (item.localKey === detailEditKey ? nextDetail : item)),
    }))

    resetDetailDraft()
    return true
  }

  function hasPartialDetailDraft() {
    return Boolean(
      (detailDraft.stockItemPresentation?.id != null && detailDraft.stockItemPresentation.id > 0)
      || detailDraft.quantity.trim() !== ''
      || detailDraft.mainItem,
    )
  }

  function startDetailEdit(item: KitItemDetailDTO) {
    setDetailDraft({
      ...item,
      quantity: formatQuantityValue(item.quantity),
      stockItemPresentation: item.stockItemPresentation ?? {
        id: item.stockItemPresentationId,
        name: item.itemLabel,
        fullDescription: item.itemLabel,
      },
    })
    setDetailEditKey(item.localKey)
    clearDetailErrors()
  }

  function removeDetail(item: KitItemDetailDTO) {
    setForm((current) => ({
      ...current,
      details: current.details.filter((currentItem) => currentItem.localKey !== item.localKey),
      deletedDetailIds: item.stockItemsKitDetailId != null && item.stockItemsKitDetailId > 0
        ? [...current.deletedDetailIds, item.stockItemsKitDetailId]
        : current.deletedDetailIds,
    }))

    if (detailEditKey === item.localKey) {
      resetDetailDraft()
    }
  }

  function openAddDetailMobile() {
    setDetailDraft(createEmptyDetail(nextLocalKey('draft')))
    setDetailEditKey(null)
    clearDetailErrors()
    setDetailMobileModalVisible(true)
  }

  function openEditDetailMobile(item: KitItemDetailDTO) {
    startDetailEdit(item)
    setDetailMobileModalVisible(true)
  }

  function closeDetailMobileModal() {
    setDetailMobileModalVisible(false)
    resetDetailDraft()
  }

  function saveDetailFromMobileModal() {
    const saved = upsertDetailDraft()
    if (saved) {
      setDetailMobileModalVisible(false)
    }
  }

  async function handleSave() {
    const nextErrors: KitsFormErrors = {}

    if (form.description.trim() === '') {
      nextErrors.description = 'La descripción es obligatoria.'
    }

    if ((form.stockItemsKitType?.id ?? -1) <= 0) {
      nextErrors.stockItemsKitType = 'El tipo de kit es obligatorio.'
    }

    if (form.details.length === 0) {
      nextErrors.detailDraft = 'El kit debe tener al menos un item.'
    }

    if (isEditingDetail || hasPartialDetailDraft()) {
      nextErrors.detailDraft = 'Primero debés agregar, guardar o cancelar el item en edición antes de guardar el kit.'
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors((current) => ({ ...current, ...nextErrors }))
      detailSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    setSaving(true)
    try {
      await saveKit(buildKitSavePayload(form))
      dispatch(setNotification({
        title: kitId > 0 ? 'Kit actualizado' : 'Kit creado',
        desc: kitId > 0 ? 'Los cambios se guardaron correctamente.' : 'El kit se creó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      onSaved?.()
      setKitId(null)
    } catch (error: unknown) {
      const response = (error as any)?.response
      const status = typeof response?.status === 'number' ? response.status : 0
      const backendMessage = typeof response?.data === 'string' ? response.data.trim() : ''
      const description = status === 409
        ? 'No se pudo guardar el kit por un conflicto de datos.'
        : status === 400 && backendMessage !== ''
          ? backendMessage
          : 'No se pudo guardar el kit.'

      dispatch(setNotification({
        title: 'Error',
        desc: description,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    } finally {
      setSaving(false)
    }
  }

  const detailColumns = useMemo<InputColumnTableProps<KitItemDetailDTO>[]>(() => ([
    {
      header: 'Item',
      className: 'kits-detail-col kits-detail-col--item',
      body: (item) => (
        <div className='kits-detail-cell'>
          {item.itemLabel}
        </div>
      ),
      input: (
        <div className='kits-detail-combo-wrap'>
          <Combobox
            className='w-full'
            optionLabel='name'
            getItems={getDetailStockItemPresentations}
            value={detailDraft.stockItemPresentation ?? defaultComboDataDTO}
            setValue={(value) => {
              setDetailDraft((current) => ({
                ...current,
                stockItemPresentation: (value as StockItemPresentationOptionDTO | null) ?? null,
                stockItemPresentationId: value?.id ?? null,
                itemLabel: value?.name ?? '',
              }))
              clearDetailErrors()
            }}
            error={Boolean(errors.detailItem)}
            caption={errors.detailItem}
          />
        </div>
      ),
    },
    {
      header: 'Cantidad',
      className: 'kits-detail-col kits-detail-col--quantity',
      body: (item) => (
        <div className='kits-detail-cell'>
          {formatQuantityForDisplay(item.quantity)}
        </div>
      ),
      input: (
        <InputTextCustom
          className='w-full'
          value={detailDraft.quantity}
          onChange={(event) => {
            const nextValue = normalizeQuantityInput(String(event?.target?.value ?? ''))
            setDetailDraft((current) => ({ ...current, quantity: nextValue }))
            clearDetailErrors()
          }}
          error={Boolean(errors.detailQuantity)}
          caption={errors.detailQuantity}
        />
      ),
    },
    {
      header: 'Item principal',
      className: 'kits-detail-col kits-detail-col--main',
      body: (item) => (
        <div className='kits-detail-switch-readonly'>
          <span>{item.mainItem ? 'Sí' : 'No'}</span>
        </div>
      ),
      input: (
        <div className='kits-detail-switch-wrap'>
          <InputSwitch
            id='kitDetailMainItemSwitchId'
            checked={detailDraft.mainItem}
            onChange={(event) => setDetailDraft((current) => ({ ...current, mainItem: Boolean(event.value) }))}
          />
          <span className='kits-detail-switch-label'>{detailDraft.mainItem ? 'Sí' : 'No'}</span>
        </div>
      ),
    },
    {
      header: '',
      className: 'kits-detail-col kits-detail-col--actions',
      input: isEditingDetail ? (
        <LocationInlineEditActions
          visible
          onConfirm={upsertDetailDraft}
          onCancel={resetDetailDraft}
          confirmTooltip='Confirmar item'
          cancelTooltip='Cancelar edición'
        />
      ) : (
        <Button
          type='button'
          icon='pi pi-plus-circle text-3xl'
          className='buttonSecondary2 p-button-rounded kits-detail-add-btn'
          onClick={() => { void upsertDetailDraft() }}
          aria-label='Agregar item'
        />
      ),
      body: (item) => (
        <div className='kits-detail-actions-body'>
          <Button
            type='button'
            icon='pi pi-file-edit'
            className='p-button-text kits-detail-action-icon-btn'
            onClick={() => startDetailEdit(item)}
            aria-label='Editar item'
          />
          <Button
            type='button'
            icon='pi pi-trash'
            className='p-button-text kits-detail-action-icon-btn'
            onClick={() => removeDetail(item)}
            aria-label='Eliminar item'
          />
        </div>
      ),
    },
  ]), [detailDraft.mainItem, detailDraft.quantity, detailDraft.stockItemPresentation, errors.detailItem, errors.detailQuantity, isEditingDetail])

  if (loading) {
    return (
      <div className='viewContainer kits-view'>
        <div className='kits-loading'>Cargando kit...</div>
      </div>
    )
  }

  return (
    <div className='viewContainer kits-view'>
      <div className='kits-back-action'>
        <Button
          type='button'
          className='btnCancel'
          label='<< Volver'
          onClick={() => setKitId(null)}
        />
      </div>

      <div className='kits-header'>
        <h1 className='kits-title'>{title}</h1>
      </div>
      <div className='kits-divider' />

      <div className='kits-status-row'>
        <div className='kits-switch-field kits-switch-field-compact'>
          <div className='kits-switch-control'>
            <InputSwitch
              id='kitsActiveSwitchId'
              checked={form.active}
              onChange={(event) => updateField('active', Boolean(event.value))}
            />
            <span className='kits-switch-status'>{form.active ? 'Activo' : 'Inactivo'}</span>
          </div>
        </div>
      </div>

      <div className='kits-crud-content'>
        <section className='kits-section kits-section-primary'>
          <div className='kits-form-grid kits-form-grid-primary'>
              <div className='kits-field kits-field-description'>
              <InputTextCustom
                className='w-full'
                label='Descripción *'
                value={form.description}
                onChange={(event) => {
                  updateField('description', event?.target?.value ?? '')
                  setErrors((current) => ({ ...current, description: undefined }))
                }}
                error={Boolean(errors.description)}
                caption={errors.description}
              />
              </div>

              <div className='kits-field kits-field-type'>
              <Combobox
                className='w-full'
                optionLabel='name'
                list={kitTypeOptions}
                value={form.stockItemsKitType ?? null}
                setValue={(value) => {
                  updateField('stockItemsKitType', (value as KitTypeOptionDTO | null) ?? null)
                  setErrors((current) => ({ ...current, stockItemsKitType: undefined }))
                }}
                label='Tipo de kit *'
                error={Boolean(errors.stockItemsKitType)}
                caption={errors.stockItemsKitType}
              />
              </div>

              <div className='kits-field kits-field-observations'>
              <InputTextCustom
                className='w-full'
                label='Observaciones'
                value={form.observations}
                onChange={(event) => updateField('observations', event?.target?.value ?? '')}
              />
              </div>
          </div>
        </section>

        <section className='kits-section kits-section-secondary' ref={detailSectionRef}>
          <div className='kits-detail-block-card'>
            <div className='kits-block-card-header'>
              <h3 className='kits-section-title'>Detalle del kit</h3>
            </div>

            {errors.detailDraft && <small className='p-error kits-detail-global-error'>{errors.detailDraft}</small>}

            {isMobile ? (
              <>
                <div className='kits-detail-mobile-toolbar'>
                  <Button
                    type='button'
                    className='kits-detail-mobile-add-btn'
                    icon='pi pi-plus-circle'
                    label='Agregar'
                    onClick={openAddDetailMobile}
                  />
                </div>

                <div className='kits-detail-mobile-card-list'>
                  {form.details.map((item) => {
                    const menuItems: LocationMobileActionItem[] = [
                      {
                        label: 'Editar',
                        icon: 'pi pi-file-edit',
                        action: () => openEditDetailMobile(item),
                      },
                      {
                        label: 'Eliminar',
                        icon: 'pi pi-trash',
                        action: () => removeDetail(item),
                      },
                    ]

                    return (
                      <article
                        key={`kit-detail-${item.localKey}`}
                        className='kits-detail-mobile-card'
                      >
                        <div className='kits-detail-mobile-card-header'>
                          <h4 className='kits-detail-mobile-card-title'>
                            {formatCardValue(item.itemLabel, 'Sin item')}
                          </h4>
                          <LocationMobileActionMenu items={menuItems} ariaLabel='Opciones del item del kit' />
                        </div>
                        <div className='kits-detail-mobile-card-row'>
                          <span className='kits-detail-mobile-card-key'>Cantidad</span>
                          <span className='kits-detail-mobile-card-value'>{formatQuantityForDisplay(item.quantity)}</span>
                        </div>
                        <div className='kits-detail-mobile-card-row'>
                          <span className='kits-detail-mobile-card-key'>Item principal</span>
                          <span className='kits-detail-mobile-card-value'>{item.mainItem ? 'Sí' : 'No'}</span>
                        </div>
                      </article>
                    )
                  })}
                  {form.details.length === 0 && (
                    <p className='kits-detail-mobile-empty'>No hay items cargados en el kit.</p>
                  )}
                </div>
              </>
            ) : (
              <InputTable
                className='kits-detail-inputtable'
                columnGap={0}
                headerDivClassName='font-semibold bg-bluegray-50'
                dividerClassName='my-1 border-divider'
                items={form.details}
                columns={detailColumns}
                onAdd={() => {}}
                divBtnClassName='kits-detail-hide-native-control'
                divider
                dividerInFirstItem
              />
            )}
          </div>
        </section>
      </div>

      <Modal
        className='kits-detail-mobile-editor-modal'
        visible={detailMobileModalVisible}
        setVisible={setDetailMobileModalVisible}
        width='w-medium'
        header={<h1 className='info-dark text-2xl'>{detailDraft.stockItemsKitDetailId != null || detailEditKey != null ? 'Editar item del kit' : 'Agregar item al kit'}</h1>}
        footerButtonLeftText='Cancelar'
        onClickLeftBtn={closeDetailMobileModal}
        footerButtonRightText={detailEditKey != null ? 'Guardar' : 'Agregar'}
        onClickRightBtn={saveDetailFromMobileModal}
        onHideCustom={closeDetailMobileModal}
      >
        <div className='kits-detail-mobile-modal-content'>
          <div>
            <label className='block mb-2'>Item <span className='text-red-500'>*</span></label>
            <Combobox
              className='w-full'
              optionLabel='name'
              getItems={getDetailStockItemPresentations}
              value={detailDraft.stockItemPresentation ?? defaultComboDataDTO}
              setValue={(value) => {
                setDetailDraft((current) => ({
                  ...current,
                  stockItemPresentation: (value as StockItemPresentationOptionDTO | null) ?? null,
                  stockItemPresentationId: value?.id ?? null,
                  itemLabel: value?.name ?? '',
                }))
                clearDetailErrors()
              }}
              error={Boolean(errors.detailItem)}
              caption={errors.detailItem}
            />
          </div>

          <div>
            <label className='block mb-2'>Cantidad <span className='text-red-500'>*</span></label>
            <InputTextCustom
              className='w-full'
              value={detailDraft.quantity}
              onChange={(event) => {
                const nextValue = normalizeQuantityInput(String(event?.target?.value ?? ''))
                setDetailDraft((current) => ({ ...current, quantity: nextValue }))
                clearDetailErrors()
              }}
              error={Boolean(errors.detailQuantity)}
              caption={errors.detailQuantity}
            />
          </div>

          <div className='kits-detail-mobile-switch-row'>
            <InputSwitch
              id='kitDetailMainItemMobileSwitchId'
              checked={detailDraft.mainItem}
              onChange={(event) => setDetailDraft((current) => ({ ...current, mainItem: Boolean(event.value) }))}
            />
            <span className='kits-detail-switch-label'>{detailDraft.mainItem ? 'Item principal: Sí' : 'Item principal: No'}</span>
          </div>
        </div>
      </Modal>

      <div className='kits-crud-actions'>
        <Button
          className='buttonMain2 kits-action-btn'
          label='Cancelar'
          onClick={() => setKitId(null)}
          disabled={saving}
        />
        <Button
          className='buttonMain3 kits-action-btn kits-primary-btn'
          label={saving ? 'Guardando...' : kitId > 0 ? 'Guardar cambios' : 'Crear kit'}
          onClick={() => { void handleSave() }}
          disabled={saving}
        />
      </div>
    </div>
  )
}
